import { useEffect, useMemo, useState } from 'react';
import {
  ACTIVITY_PHASES,
  ACTIVITY_STATUS_LABELS,
  AS_ROADMAP_STEP_SHORT_LABELS,
  activityProgressFromActions,
  activityStatusFromProgress,
  currentRoadmapStep,
  normalizeActivity,
  resolveRoadmapSteps,
  type AccompanimentActivity,
  type ActivityActionItem,
  type ActivityPhaseKey,
  type ActivityStatus,
  type AsRoadmapStepKey,
  type RoadmapObjective,
  type RoadmapStepView,
} from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useToast } from '@/features/ui/toast/useToast';

type ActivityForm = {
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  expected_result: string;
  obtained_result: string;
  observations: string;
  status: ActivityStatus;
  objective_id: string;
  action_labels: string;
};

const emptyForm = (objectiveId = ''): ActivityForm => ({
  title: '',
  description: '',
  start_date: '',
  end_date: '',
  expected_result: '',
  obtained_result: '',
  observations: '',
  status: 'pending',
  objective_id: objectiveId,
  action_labels: '',
});

function formatDateShort(iso: string | null | undefined) {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function newActionId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function parseActionLabels(raw: string): ActivityActionItem[] {
  return raw
    .split(/\n|;/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((label) => ({ id: newActionId(), label, done: false }));
}

function statusChipClass(status: ActivityStatus) {
  if (status === 'done') return 'done';
  if (status === 'in_progress') return 'progress';
  if (status === 'blocked') return 'blocked';
  return 'pending';
}

function MiniRing({ value }: { value: number }) {
  const r = 18;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const offset = c - (pct / 100) * c;
  return (
    <svg className="as-act-mini-ring" viewBox="0 0 48 48" aria-hidden>
      <circle cx="24" cy="24" r={r} fill="none" stroke="#e5eef6" strokeWidth="5" />
      <circle
        cx="24"
        cy="24"
        r={r}
        fill="none"
        stroke="#2f6fed"
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform="rotate(-90 24 24)"
      />
      <text x="24" y="28" textAnchor="middle" className="as-act-mini-ring-text">
        {pct}%
      </text>
    </svg>
  );
}

interface Props {
  accompanimentId: string;
  entrepreneurName: string;
  formulaKey?: string | null;
  roadmap: RoadmapObjective[];
  activities: AccompanimentActivity[];
  onChange: () => Promise<void> | void;
  /** Admin / conseiller : ajout & édition. Utilisateur : suivi + enregistrement des progrès. */
  mode?: 'user' | 'admin';
  journeyStatusLabel?: string;
}

export function ActivitiesPanel({
  accompanimentId,
  entrepreneurName,
  formulaKey,
  roadmap,
  activities: rawActivities,
  onChange,
  mode = 'user',
  journeyStatusLabel = 'En cours',
}: Props) {
  const toast = useToast();
  const isAdmin = mode === 'admin';
  const steps = useMemo(() => resolveRoadmapSteps(roadmap, formulaKey), [roadmap, formulaKey]);
  const includedSteps = useMemo(() => steps.filter((s) => s.included), [steps]);
  const current = currentRoadmapStep(steps);

  const activities = useMemo(
    () => rawActivities.map((a) => normalizeActivity(a)),
    [rawActivities],
  );

  const [selectedKey, setSelectedKey] = useState<AsRoadmapStepKey | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftPhase, setDraftPhase] = useState<ActivityPhaseKey>('clarifier');
  const [draftActions, setDraftActions] = useState<ActivityActionItem[]>([]);
  const [newActionLabel, setNewActionLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [form, setForm] = useState<ActivityForm>(() => emptyForm());
  const [menuId, setMenuId] = useState<string | null>(null);

  useEffect(() => {
    const key = current?.key ?? includedSteps[0]?.key ?? null;
    setSelectedKey((prev) => prev ?? key);
  }, [current?.key, includedSteps]);

  const selectedStep: RoadmapStepView | null =
    includedSteps.find((s) => s.key === selectedKey) ?? includedSteps[0] ?? null;

  const stepActivities = useMemo(() => {
    if (!selectedStep) return activities;
    const oid = selectedStep.objectiveId;
    const fallbackKey = current?.key ?? includedSteps[0]?.key;
    return activities.filter((a) => {
      if (!a.objective_id) return selectedStep.key === fallbackKey;
      return oid ? a.objective_id === oid : false;
    });
  }, [activities, selectedStep, current?.key, includedSteps]);

  const planned = stepActivities.length;
  const doneCount = stepActivities.filter((a) => a.status === 'done').length;
  const avgProgress = planned
    ? Math.round(stepActivities.reduce((sum, a) => sum + a.progress, 0) / planned)
    : 0;
  const nextActivity =
    stepActivities.find((a) => a.status !== 'done') ?? stepActivities[0] ?? null;

  const selected = activities.find((a) => a.id === selectedId) ?? null;

  useEffect(() => {
    if (!selected) return;
    setDraftPhase(selected.current_phase);
    setDraftActions(selected.action_items.map((x) => ({ ...x })));
  }, [selectedId, selected?.updated_at]);

  function openView(activity: AccompanimentActivity) {
    setSelectedId(activity.id);
    setMenuId(null);
  }

  function closeDrawer() {
    setSelectedId(null);
  }

  function openCreate() {
    setForm(emptyForm(selectedStep?.objectiveId || ''));
    setModal('create');
  }

  function openEdit(activity: AccompanimentActivity) {
    setForm({
      title: activity.title,
      description: activity.description || '',
      start_date: activity.start_date || '',
      end_date: activity.end_date || activity.due_date || '',
      expected_result: activity.expected_result || '',
      obtained_result: activity.obtained_result || '',
      observations: activity.observations || '',
      status: activity.status,
      objective_id: activity.objective_id || selectedStep?.objectiveId || '',
      action_labels: activity.action_items.map((a) => a.label).join('\n'),
    });
    setSelectedId(activity.id);
    setModal('edit');
    setMenuId(null);
  }

  async function saveForm() {
    if (!form.title.trim()) {
      toast.error('Titre d’activité requis.');
      return;
    }
    setLoading(true);
    try {
      const action_items =
        modal === 'edit' && selected
          ? selected.action_items.length
            ? selected.action_items
            : parseActionLabels(form.action_labels)
          : parseActionLabels(form.action_labels);
      const progress = activityProgressFromActions(action_items);
      const status =
        form.status === 'blocked'
          ? 'blocked'
          : activityStatusFromProgress(progress, form.status);
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        due_date: form.end_date || null,
        expected_result: form.expected_result.trim() || null,
        obtained_result: form.obtained_result.trim() || null,
        observations: form.observations.trim() || null,
        objective_id: form.objective_id || null,
        action_items,
        progress,
        status,
        completed_at: status === 'done' ? new Date().toISOString() : null,
      };

      if (modal === 'create') {
        const { error: err } = await kuunda.from('accompaniment_activities').insert({
          ...payload,
          accompaniment_id: accompanimentId,
          sort_order: activities.length + 1,
          current_phase: 'clarifier',
        });
        if (err) throw err;
        toast.success('Activité ajoutée.');
      } else if (selected) {
        const { error: err } = await kuunda
          .from('accompaniment_activities')
          .update(payload)
          .eq('id', selected.id);
        if (err) throw err;
        toast.success('Activité mise à jour.');
      }
      setModal(null);
      await onChange();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible');
    } finally {
      setLoading(false);
    }
  }

  async function saveProgress() {
    if (!selected) return;
    setLoading(true);
    try {
      const progress = activityProgressFromActions(draftActions);
      const status =
        selected.status === 'blocked'
          ? 'blocked'
          : activityStatusFromProgress(progress, selected.status);
      const { error: err } = await kuunda
        .from('accompaniment_activities')
        .update({
          current_phase: draftPhase,
          action_items: draftActions,
          progress,
          status,
          completed_at: status === 'done' ? new Date().toISOString() : null,
        })
        .eq('id', selected.id);
      if (err) throw err;
      toast.success('Progrès enregistrés.');
      await onChange();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible');
    } finally {
      setLoading(false);
    }
  }

  async function deleteActivity(id: string) {
    if (!isAdmin) return;
    if (!window.confirm('Supprimer cette activité ?')) return;
    setLoading(true);
    try {
      const { error: err } = await kuunda.from('accompaniment_activities').delete().eq('id', id);
      if (err) throw err;
      if (selectedId === id) setSelectedId(null);
      setMenuId(null);
      await onChange();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Suppression impossible');
    } finally {
      setLoading(false);
    }
  }

  function toggleAction(id: string) {
    setDraftActions((prev) => prev.map((a) => (a.id === id ? { ...a, done: !a.done } : a)));
  }

  function addAction() {
    const label = newActionLabel.trim();
    if (!label) return;
    setDraftActions((prev) => [...prev, { id: newActionId(), label, done: false }]);
    setNewActionLabel('');
  }

  function setPhase(key: ActivityPhaseKey) {
    setDraftPhase(key);
  }

  const phaseIndex = ACTIVITY_PHASES.findIndex((p) => p.key === draftPhase);

  const journeyLabel =
    includedSteps.every((s) => s.status === 'done') && includedSteps.length
      ? 'Terminé'
      : journeyStatusLabel;

  return (
    <div className={`as-act ${selected ? 'has-drawer' : ''}`}>
      <div className="as-act-top">
        <div className="as-act-title-row">
          <h3 className="as-act-title">Parcours d&apos;accompagnement – {entrepreneurName}</h3>
          <span className="as-act-journey-badge">{journeyLabel}</span>
        </div>

        <div className="as-act-stepper" role="tablist" aria-label="Étapes du parcours">
          {steps.map((step, i) => {
            const short = AS_ROADMAP_STEP_SHORT_LABELS[step.key] ?? step.title;
            const active = selectedStep?.key === step.key;
            const done = step.included && step.status === 'done';
            const disabled = !step.included;
            return (
              <button
                key={step.key}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={disabled}
                className={`as-act-step ${done ? 'is-done' : ''} ${active ? 'is-active' : ''} ${disabled ? 'is-na' : ''}`}
                onClick={() => {
                  if (disabled) return;
                  setSelectedKey(step.key);
                  setSelectedId(null);
                }}
              >
                {i < steps.length - 1 ? <span className="as-act-step-line" aria-hidden /> : null}
                <span className="as-act-step-num">{step.order}</span>
                {done ? (
                  <span className="as-act-step-check" aria-hidden>
                    ✓
                  </span>
                ) : null}
                <span className="as-act-step-label">{short}</span>
              </button>
            );
          })}
        </div>
      </div>

      {selectedStep ? (
        <section className="as-act-summary">
          <div className="as-act-summary-main">
            <div className="as-act-summary-icon" aria-hidden>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"
                  stroke="#2f6fed"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <path
                  d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"
                  stroke="#2f6fed"
                  strokeWidth="1.8"
                />
                <path d="M8 7h8M8 11h5" stroke="#2f6fed" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <h4>
                Étape {selectedStep.order} : {selectedStep.title}
              </h4>
              <p>{selectedStep.description}</p>
            </div>
          </div>
          <div className="as-act-kpis">
            <div className="as-act-kpi">
              <p className="as-act-kpi-label">Activités prévues</p>
              <p className="as-act-kpi-value">{planned}</p>
            </div>
            <div className="as-act-kpi">
              <p className="as-act-kpi-label">Activités terminées</p>
              <p className="as-act-kpi-value">{doneCount}</p>
            </div>
            <div className="as-act-kpi as-act-kpi-ring">
              <p className="as-act-kpi-label">Taux d&apos;avancement</p>
              <MiniRing value={avgProgress} />
            </div>
            <div className="as-act-kpi">
              <p className="as-act-kpi-label">Prochaine activité</p>
              {nextActivity ? (
                <>
                  <p className="as-act-kpi-next">{nextActivity.title}</p>
                  <p className="as-act-kpi-date">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                    {formatDateShort(nextActivity.start_date || nextActivity.end_date || nextActivity.due_date)}
                  </p>
                </>
              ) : (
                <p className="as-act-kpi-next muted">—</p>
              )}
            </div>
          </div>
        </section>
      ) : (
        <p className="muted">Aucune étape incluse dans ce parcours.</p>
      )}

      <section className="as-act-plan">
        <div className="as-act-plan-head">
          <h4>Plan d&apos;activités de l&apos;étape</h4>
          {isAdmin ? (
            <button type="button" className="btn btn-primary" disabled={loading || !selectedStep} onClick={openCreate}>
              + Ajouter une activité
            </button>
          ) : null}
        </div>

        {stepActivities.length === 0 ? (
          <p className="muted">Aucune activité pour cette étape.</p>
        ) : (
          <div className="as-act-table-wrap">
            <table className="as-act-table">
              <thead>
                <tr>
                  <th rowSpan={2}>#</th>
                  <th rowSpan={2}>Activités</th>
                  <th colSpan={2}>Planification</th>
                  <th rowSpan={2}>Résultat attendu</th>
                  <th rowSpan={2}>État de mise en œuvre</th>
                  <th rowSpan={2}>Résultats obtenus</th>
                  <th rowSpan={2}>Observations</th>
                  <th rowSpan={2}>Actions</th>
                </tr>
                <tr className="as-act-subhead">
                  <th>Date début</th>
                  <th>Date fin</th>
                </tr>
              </thead>
              <tbody>
                {stepActivities.map((a, idx) => (
                  <tr key={a.id} className={selectedId === a.id ? 'is-selected' : undefined}>
                    <td className="as-act-col-num">{idx + 1}</td>
                    <td>
                      <strong>{a.title}</strong>
                    </td>
                    <td>{formatDateShort(a.start_date)}</td>
                    <td>{formatDateShort(a.end_date || a.due_date)}</td>
                    <td className="as-act-cell-muted">{a.expected_result || '—'}</td>
                    <td>
                      <div className="as-act-status">
                        <span className={`as-act-status-chip ${statusChipClass(a.status)}`}>
                          {ACTIVITY_STATUS_LABELS[a.status]}
                        </span>
                        <div className="as-act-bar" aria-hidden>
                          <div
                            className={`as-act-bar-fill ${statusChipClass(a.status)}`}
                            style={{ width: `${a.progress}%` }}
                          />
                        </div>
                        <span className="as-act-pct">{a.progress}%</span>
                      </div>
                    </td>
                    <td className="as-act-cell-muted">{a.obtained_result || '—'}</td>
                    <td className="as-act-cell-muted">{a.observations || '—'}</td>
                    <td>
                      <div className="as-act-row-actions">
                        <button
                          type="button"
                          className="as-act-icon-btn"
                          title="Voir"
                          aria-label="Voir l’activité"
                          onClick={() => openView(a)}
                        >
                          <EyeIcon />
                        </button>
                        {isAdmin ? (
                          <button
                            type="button"
                            className="as-act-icon-btn"
                            title="Modifier"
                            aria-label="Modifier l’activité"
                            onClick={() => openEdit(a)}
                          >
                            <PencilIcon />
                          </button>
                        ) : null}
                        <div style={{ position: 'relative' }}>
                          <button
                            type="button"
                            className="as-act-icon-btn"
                            title="Plus"
                            aria-label="Plus d’actions"
                            onClick={() => setMenuId((m) => (m === a.id ? null : a.id))}
                          >
                            <DotsIcon />
                          </button>
                          {menuId === a.id ? (
                            <div className="as-act-menu">
                              <button type="button" onClick={() => openView(a)}>
                                Voir le détail
                              </button>
                              {isAdmin ? (
                                <>
                                  <button type="button" onClick={() => openEdit(a)}>
                                    Modifier
                                  </button>
                                  <button type="button" className="is-danger" onClick={() => void deleteActivity(a.id)}>
                                    Supprimer
                                  </button>
                                </>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selected ? (
        <aside className="as-act-drawer" aria-label="Détail de l’activité">
          <div className="as-act-drawer-head">
            <h4>Détail de l&apos;activité</h4>
            <button type="button" className="as-act-icon-btn" aria-label="Fermer" onClick={closeDrawer}>
              ✕
            </button>
          </div>
          <p className="as-act-drawer-sub">
            <span>Activité sélectionnée</span>
            <strong>{selected.title}</strong>
          </p>

          <ol className="as-act-phases">
            {ACTIVITY_PHASES.map((phase, i) => {
              const isDone = i < phaseIndex;
              const isCurrent = i === phaseIndex;
              return (
                <li key={phase.key} className={`${isDone ? 'is-done' : ''} ${isCurrent ? 'is-current' : ''}`}>
                  <button
                    type="button"
                    className="as-act-phase-dot"
                    aria-label={phase.title}
                    onClick={() => setPhase(phase.key)}
                  >
                    {isDone ? '✓' : ''}
                  </button>
                  <div>
                    <button type="button" className="as-act-phase-title" onClick={() => setPhase(phase.key)}>
                      {phase.title}
                    </button>
                    <p>{phase.description}</p>
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="as-act-actions-block">
            <h5>Actions à mener</h5>
            {draftActions.length === 0 ? (
              <p className="muted" style={{ fontSize: '0.85rem' }}>
                Aucune action définie
                {isAdmin ? ' — ajoutez-en ci-dessous ou via « Modifier ».' : '.'}
              </p>
            ) : (
              <ul className="as-act-checklist">
                {draftActions.map((item) => (
                  <li key={item.id}>
                    <label className={item.done ? 'is-done' : undefined}>
                      <input
                        type="checkbox"
                        checked={item.done}
                        onChange={() => toggleAction(item.id)}
                      />
                      <span>{item.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <div className="as-act-add-action">
              <input
                value={newActionLabel}
                onChange={(e) => setNewActionLabel(e.target.value)}
                placeholder="Nouvelle action…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAction();
                  }
                }}
              />
              <button type="button" className="btn btn-ghost" onClick={addAction}>
                +
              </button>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-primary as-act-save"
            disabled={loading}
            onClick={() => void saveProgress()}
          >
            Enregistrer les progrès
          </button>
        </aside>
      ) : null}

      {modal ? (
        <div className="as-act-modal-backdrop" role="presentation" onClick={() => setModal(null)}>
          <div
            className="as-act-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="as-act-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="as-act-modal-title" style={{ marginTop: 0 }}>
              {modal === 'create' ? 'Ajouter une activité' : 'Modifier l’activité'}
            </h4>
            <div className="as-act-form-grid">
              <label className="as-act-span-2">
                Titre
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex. Gestion financière de base"
                />
              </label>
              <label>
                Date début
                <input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </label>
              <label>
                Date fin
                <input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </label>
              <label className="as-act-span-2">
                Étape liée
                <select
                  value={form.objective_id}
                  onChange={(e) => setForm({ ...form, objective_id: e.target.value })}
                >
                  <option value="">— Non liée —</option>
                  {includedSteps.map((s) => (
                    <option key={s.key} value={s.objectiveId}>
                      {s.order}. {s.title}
                    </option>
                  ))}
                </select>
              </label>
              <label className="as-act-span-2">
                Résultat attendu
                <textarea
                  rows={2}
                  value={form.expected_result}
                  onChange={(e) => setForm({ ...form, expected_result: e.target.value })}
                />
              </label>
              <label className="as-act-span-2">
                Résultats obtenus
                <textarea
                  rows={2}
                  value={form.obtained_result}
                  onChange={(e) => setForm({ ...form, obtained_result: e.target.value })}
                />
              </label>
              <label className="as-act-span-2">
                Observations
                <textarea
                  rows={2}
                  value={form.observations}
                  onChange={(e) => setForm({ ...form, observations: e.target.value })}
                />
              </label>
              {modal === 'create' ? (
                <label className="as-act-span-2">
                  Actions à mener (une par ligne)
                  <textarea
                    rows={4}
                    value={form.action_labels}
                    onChange={(e) => setForm({ ...form, action_labels: e.target.value })}
                    placeholder={'Préparer le budget prévisionnel\nÉlaborer le plan de trésorerie'}
                  />
                </label>
              ) : null}
              <label>
                Statut
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as ActivityStatus })}
                >
                  {(Object.keys(ACTIVITY_STATUS_LABELS) as ActivityStatus[]).map((k) => (
                    <option key={k} value={k}>
                      {ACTIVITY_STATUS_LABELS[k]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="as-act-span-2">
                Description
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </label>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button type="button" className="btn btn-primary" disabled={loading} onClick={() => void saveForm()}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M13 6.5 16.5 10" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="12" cy="5" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="12" cy="19" r="1.8" />
    </svg>
  );
}
